import { useMemo } from 'react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { NumberFormatter, Stack, Text } from '@mantine/core'
import { tableDefaultAlertBannerProps, tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { ContractedLot, ContractedLotResult } from '@loom-api-types'
import { contractedLotResultToString } from '@shared/constants/contracted-lot-result/contracted-lot-result'

type ContractedLotsSubtableProps = {
  data: ContractedLot[]
  totalBalesAccepted?: number | undefined
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  includeRedaType?: boolean
}

export const ContractedLotsSubtable = ({
  data = [],
  totalBalesAccepted = undefined,
  noDataMessage = 'No values to display',
  isLoading,
  isError,
  includeRedaType = false,
}: ContractedLotsSubtableProps) => {
  const baseColumns: MRT_ColumnDef<ContractedLot>[] = [
    {
      id: 'reference',
      accessorKey: 'reference',
      header: 'Reference',
    },
    {
      id: 'bales',
      accessorKey: 'bales',
      header: 'Bales',
      mantineTableHeadCellProps: {
        align: 'right',
      },
      mantineTableBodyCellProps: {
        align: 'right',
      },
    },
    {
      id: 'lineNumber',
      accessorKey: 'lineNumber',
      header: 'Line Number',
    },
    {
      id: 'netKg',
      accessorKey: 'netKg',
      header: 'Net KG',
      Cell: ({ cell }) => <NumberFormatter value={cell.getValue<number>()} thousandSeparator />,
      mantineTableHeadCellProps: {
        align: 'right',
      },
      mantineTableBodyCellProps: {
        align: 'right',
      },
    },
    {
      id: 'micron',
      accessorKey: 'micron',
      header: 'Micron',
      Cell: ({ cell }) => <NumberFormatter value={cell.getValue<number>()} decimalScale={2} />,
      mantineTableHeadCellProps: {
        align: 'right',
      },
      mantineTableBodyCellProps: {
        align: 'right',
      },
    },
    {
      id: 'result',
      accessorKey: 'result',
      header: 'Result',
      Cell: ({ cell }) => contractedLotResultToString(cell.getValue<ContractedLotResult>()),
    },
    {
      id: 'redaType',
      accessorKey: 'redaType',
      header: 'Reda Type',
    },
    {
      id: 'priceClean',
      accessorKey: 'priceClean',
      header: 'Price Clean',
      Cell: ({ cell }) => (
        <NumberFormatter suffix='c' value={cell.getValue<number>()} thousandSeparator />
      ),
      mantineTableHeadCellProps: {
        align: 'right',
      },
      mantineTableBodyCellProps: {
        align: 'right',
      },
    },
    {
      id: 'paymentDate',
      accessorKey: 'paymentDate',
      header: 'Payment Date',
      Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
    },
  ]

  const columns = useMemo<MRT_ColumnDef<ContractedLot>[]>(() => {
    return includeRedaType ? baseColumns : baseColumns.filter((column) => column.id !== 'redaType')
  }, [includeRedaType])

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Contracted lots',
    },
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
  })

  return (
    <Stack>
      <MantineReactTable table={table} key={includeRedaType ? 'with-reda' : 'no-reda'} />
      {totalBalesAccepted !== undefined && (
        <Text size='sm'>
          <NumberFormatter value={totalBalesAccepted} thousandSeparator /> total bales accepted
        </Text>
      )}
    </Stack>
  )
}
